import { db, firebase_rdb } from "../../../Config/Config";
import { performAdminDataMutation } from "../../../utils/adminDataMutationClient";

export const CLIENT_ID = "maxpark-220731";
export const FIRESTORE_USERS_PATH = `clients/${CLIENT_ID}/users`;
export const RTDB_AGENTS_PATH = `clients/${CLIENT_ID}/agents`;
const USERS_CACHE_TTL_MS = 60 * 1000;
const USER_MUTATION_FIELDS = new Set([
  "firstname",
  "lastname",
  "email",
  "useremail",
  "phone",
  "photo",
  "hiredate",
  "usertype",
  "userstatus",
  "status",
  "salary",
  "bonus",
  "terminationdate",
  "sendpassword",
]);

let usersCache = null;

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const firstValue = (...values) => {
  const value = values.find(hasValue);
  return value !== undefined ? value : values[values.length - 1];
};

const normalizeComparableValue = (value) => String(value || "").trim().toLowerCase();

const hasAnyFieldValue = (value = {}, fields = []) =>
  fields.some((field) => hasValue(value[field]));

const removeUndefinedValues = (value) => {
  if (value === undefined) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value.map(removeUndefinedValues).filter((item) => item !== undefined);
  }

  if (
    value &&
    typeof value === "object" &&
    !(value instanceof Date) &&
    !value.toDate
  ) {
    return Object.entries(value).reduce((cleaned, [key, childValue]) => {
      const cleanedValue = removeUndefinedValues(childValue);

      if (cleanedValue !== undefined) {
        cleaned[key] = cleanedValue;
      }

      return cleaned;
    }, {});
  }

  return value;
};

const sanitizeUserMutationFields = (fields = {}) =>
  Object.entries(removeUndefinedValues(fields) || {}).reduce((result, [key, value]) => {
    if (USER_MUTATION_FIELDS.has(key)) {
      result[key] = value;
    }
    return result;
  }, {});

const unwrapUserData = (value = {}) => {
  if (value.userData && typeof value.userData === "object") {
    return value.userData;
  }

  if (value.profile && typeof value.profile === "object") {
    return { ...value, ...value.profile };
  }

  return value || {};
};

const publicUserData = (value = {}) =>
  Object.fromEntries(
    Object.entries(value).filter(([key]) => {
      const normalizedKey = String(key).toLowerCase();
      return normalizedKey !== "customclaims" &&
        !normalizedKey.startsWith("payment") &&
        !normalizedKey.startsWith("paiement");
    })
  );

const profileIdentityFields = [
  "employeeid",
  "employeeID",
  "employeeId",
  "employeeNumber",
  "matricule",
  "firstname",
  "firstName",
  "prenom",
  "agentfirstname",
  "lastname",
  "lastName",
  "nom",
  "agentlastname",
  "hiredate",
  "hireDate",
  "startDate",
  "usertype",
  "userType",
  "type",
  "role",
  "userstatus",
  "userStatus",
  "status",
];

const hasProfileIdentity = (value = {}) => {
  const userData = unwrapUserData(value);
  return hasAnyFieldValue(userData, profileIdentityFields);
};

const hasNormalizedProfileIdentity = (user = {}) =>
  hasAnyFieldValue(user, [
    "employeeid",
    "firstname",
    "lastname",
    "hiredate",
    "usertype",
    "userstatus",
    "status",
  ]);

export const serializeUserDate = (value) => {
  if (!value) {
    return "";
  }

  if (value.toDate) {
    return value.toDate().toISOString();
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : value.toISOString();
  }

  if (typeof value === "object" && value.seconds) {
    return new Date(value.seconds * 1000).toISOString();
  }

  return value;
};

export const parseUserDate = (value) => {
  if (!value) {
    return "";
  }

  if (value.toDate) {
    return value.toDate();
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : value;
  }

  if (typeof value === "object" && value.seconds) {
    return new Date(value.seconds * 1000);
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date;
};

const formatDate = (value) => {
  const date = parseUserDate(value);
  return date instanceof Date ? date.toISOString().slice(0, 10) : value || "";
};

export const isTerminatedStatus = (status) =>
  String(status || "").toLowerCase().includes("termin");

export const normalizeUser = (value = {}, uid = "") => {
  const userData = unwrapUserData(value);
  const visibleUserData = publicUserData(userData);
  const sourceId = String(uid || "").trim();
  const storedUid = String(userData.uid || "").trim();
  const employeeid = String(
    firstValue(
      userData.employeeid,
      userData.employeeID,
      userData.employeeId,
      userData.employeeNumber,
      userData.matricule,
      userData.id
    ) || ""
  );
  // Legacy profiles stored the employee number in uid. It is an alias,
  // not a second Firebase Auth identity conflicting with the migrated uid.
  const authUid = storedUid && storedUid !== employeeid ? storedUid : '';
  const userID = firstValue(authUid, sourceId, employeeid);
  const email = firstValue(
    userData.email,
    userData.useremail,
    userData.courriel,
    userData.agent,
    ""
  );
  const userstatus = firstValue(userData.userstatus, userData.status, userData.userStatus, "");
  const terminationdate = firstValue(
    userData.terminationdate,
    userData.terminationDate,
    userData.endDate,
    ""
  );

  return {
    ...visibleUserData,
    uid: userID,
    authUid,
    _sourceId: sourceId,
    employeeid,
    firstname: firstValue(
      userData.firstname,
      userData.firstName,
      userData.prenom,
      userData.agentfirstname,
      ""
    ),
    lastname: firstValue(
      userData.lastname,
      userData.lastName,
      userData.nom,
      userData.agentlastname,
      ""
    ),
    email,
    useremail: firstValue(userData.useremail, email, ""),
    phone: firstValue(userData.phone, userData.telephone, userData.tel, ""),
    photo: firstValue(userData.photo, userData.picture, userData.avatar, ""),
    hiredate: formatDate(firstValue(userData.hiredate, userData.hireDate, userData.startDate, "")),
    hiredateRaw: firstValue(userData.hiredate, userData.hireDate, userData.startDate, ""),
    salary: firstValue(userData.salary, userData.salaire, ""),
    bonus: firstValue(userData.bonus, "0"),
    usertype: firstValue(userData.usertype, userData.userType, userData.type, userData.role, ""),
    userstatus,
    status: userstatus,
    terminated:
      Boolean(firstValue(userData.terminated, false)) || isTerminatedStatus(userstatus),
    terminationdate: formatDate(terminationdate),
    terminationdateRaw: terminationdate,
  };
};

const mergeUser = (base = {}, next = {}) => {
  const merged = { ...base };

  Object.entries(next).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      merged[key] = value;
    }
  });

  return merged;
};

const sortUsers = (users) =>
  users.sort((a, b) => {
    const first = hasValue(a.employeeid) ? Number(a.employeeid) : Number.NaN;
    const second = hasValue(b.employeeid) ? Number(b.employeeid) : Number.NaN;

    if (Number.isFinite(first) && Number.isFinite(second)) {
      return first - second;
    }

    return String(a.employeeid || a.uid).localeCompare(String(b.employeeid || b.uid));
  });

const getCachedUsers = () => {
  if (!usersCache || Date.now() - usersCache.cachedAt > USERS_CACHE_TTL_MS) {
    return null;
  }

  return {
    users: usersCache.users,
    errors: usersCache.errors,
  };
};

const rememberUsers = (result) => {
  usersCache = {
    ...result,
    cachedAt: Date.now(),
  };

  return result;
};

export const clearUsersCache = () => {
  usersCache = null;
};

const mergeUsersFromSettledResults = (results) => {
  const copies = results
    .filter((result) => result.status === "fulfilled")
    .flatMap((result) => result.value);
  const parents = copies.map((_, index) => index);
  const findRoot = (index) => {
    let root = index;
    while (parents[root] !== root) root = parents[root];
    while (parents[index] !== index) {
      const next = parents[index];
      parents[index] = root;
      index = next;
    }
    return root;
  };
  const union = (left, right) => {
    const leftRoot = findRoot(left);
    const rightRoot = findRoot(right);
    if (leftRoot !== rightRoot) parents[rightRoot] = leftRoot;
  };
  const aliasOwner = new Map();
  const aliasesFor = (user) => Array.from(new Set([
    user.authUid,
    user._sourceId,
    user.employeeid,
    user.uid,
  ].map(normalizeComparableValue).filter(Boolean)));

  copies.forEach((user, index) => {
    aliasesFor(user).forEach((alias) => {
      if (aliasOwner.has(alias)) {
        union(index, aliasOwner.get(alias));
      } else {
        aliasOwner.set(alias, index);
      }
    });
  });

  const groups = new Map();
  copies.forEach((user, index) => {
    const root = findRoot(index);
    groups.set(root, [...(groups.get(root) || []), user]);
  });

  const errors = results
    .filter((result) => result.status === "rejected")
    .map((result) => result.reason);
  const users = [];

  groups.forEach((group) => {
    const authUids = new Set(group.map((user) => String(user.authUid || "").trim()).filter(Boolean));
    const employeeIds = new Set(group.map((user) => String(user.employeeid || "").trim()).filter(Boolean));
    if (authUids.size > 1 || employeeIds.size > 1) {
      errors.push(Object.assign(new Error("User identity aliases require reconciliation"), {
        code: "USER_IDENTITY_RECONCILIATION_REQUIRED",
      }));
      return;
    }

    const merged = group.reduce((current, user) => mergeUser(current, user), {});
    const authUid = Array.from(authUids)[0] || "";
    const employeeid = Array.from(employeeIds)[0] || String(merged.employeeid || "");
    const sourceIds = group.map((user) => String(user._sourceId || "").trim()).filter(Boolean);
    const archived = group.some((user) => user.archived === true);
    const { _sourceId, ...visibleMerged } = merged;

    users.push({
      ...visibleMerged,
      uid: authUid || String(merged.uid || sourceIds[0] || employeeid),
      authUid,
      employeeid,
      archived,
    });
  });

  if (!copies.length && errors.length === results.length) {
    throw errors[0];
  }

  return {
    users: sortUsers(users.filter((user) => user.archived !== true)),
    errors,
  };
};

const createUserSourcePromises = (options = {}) => [
  loadRealtimeUsers(),
  loadFirestoreUsers(options),
];

export const loadRealtimeUsers = async () => {
  const snapshot = await firebase_rdb.child(RTDB_AGENTS_PATH).once("value");
  const agents = snapshot.val() || {};

  return Object.entries(agents)
    .filter(([, value]) => value?.archived === true || hasProfileIdentity(value))
    .map(([uid, value]) => normalizeUser(value, uid))
    .filter((user) => user.archived === true || hasNormalizedProfileIdentity(user));
};

export const loadFirestoreUsers = async ({ requireServer = false } = {}) => {
  const collection = db.collection(FIRESTORE_USERS_PATH);
  const snapshot = requireServer ? await collection.get({ source: 'server' }) : await collection.get();
  return snapshot.docs
    .map((userDoc) => normalizeUser(userDoc.data(), userDoc.id))
    .filter((user) => user.archived === true || hasNormalizedProfileIdentity(user));
};

export const loadUsers = async ({ forceRefresh = false } = {}) => {
  const cachedUsers = forceRefresh ? null : getCachedUsers();

  if (cachedUsers) {
    return cachedUsers;
  }

  const results = await Promise.allSettled(createUserSourcePromises());
  return rememberUsers(mergeUsersFromSettledResults(results));
};

export const loadUsersProgressively = async ({
  requireServer = false,
  forceRefresh = false,
  onUpdate,
} = {}) => {
  const cachedUsers = forceRefresh ? null : getCachedUsers();

  if (cachedUsers) {
    if (onUpdate) {
      onUpdate({ ...cachedUsers, complete: true, fromCache: true });
    }

    return cachedUsers;
  }

  const userSourcePromises = createUserSourcePromises({ requireServer });
  const settledResults = new Array(userSourcePromises.length);
  const sourcePromises = userSourcePromises.map((promise, index) =>
    promise
      .then((value) => {
        settledResults[index] = { status: "fulfilled", value };
        const partialResults = settledResults.filter(Boolean);
        const partialUsers = mergeUsersFromSettledResults(partialResults);

        if (partialUsers.users.length) {
          if (onUpdate) {
            onUpdate({ ...partialUsers, complete: partialResults.length === settledResults.length });
          }
        }

        return value;
      })
      .catch((reason) => {
        settledResults[index] = { status: "rejected", reason };
        throw reason;
      })
  );

  const finalResults = await Promise.allSettled(sourcePromises);
  const finalUsers = rememberUsers(mergeUsersFromSettledResults(finalResults));

  if (onUpdate) {
    onUpdate({ ...finalUsers, complete: true });
  }

  return finalUsers;
};

export const getNextEmployeeID = async () => {
  const { users } = await loadUsers();
  const employeeIDs = users
    .map((user) => Number(user.employeeid))
    .filter((employeeID) => Number.isFinite(employeeID));

  return employeeIDs.length ? Math.max(...employeeIDs) + 1 : 1000;
};

export const findUserByEmployeeID = async (employeeID, { forceRefresh = false } = {}) => {
  const { users } = await loadUsers({ forceRefresh });
  return users.find(
    (user) =>
      String(user.employeeid) === String(employeeID) ||
      String(user.uid) === String(employeeID)
  );
};

export const getUserProfilePath = (user = {}) => String(user?.employeeid || user?.uid || "");

export const findUserForAuthUser = async (authUser) => {
  if (!authUser) {
    return null;
  }

  // The directory contains profiles only, unlike the RTDB agent tree and its tickets.
  const { users } = mergeUsersFromSettledResults([{
    status: 'fulfilled', value: await loadFirestoreUsers().catch(() => []),
  }]);
  const authUid = normalizeComparableValue(authUser.uid);
  const authEmail = normalizeComparableValue(authUser.email);

  const matchesAuthUser = (user) => {
    const userIds = [
      user.uid,
      user.employeeid,
      user.employeeID,
      user.employeeId,
      user.id,
    ].map(normalizeComparableValue).filter(Boolean);

    const userEmails = [
      user.email,
      user.useremail,
      user.courriel,
      user.agent,
    ].map(normalizeComparableValue).filter(Boolean);

    return (
      (authUid && userIds.includes(authUid)) ||
      (authEmail && userEmails.includes(authEmail))
    );
  };
  const profile = users.find(matchesAuthUser);
  if (profile) return profile;
  const snapshot = await firebase_rdb.child(`${RTDB_AGENTS_PATH}/${authUser.uid}`).once('value');
  const value = snapshot.val();
  if (!value || value.archived || !hasProfileIdentity(value)) return null;
  const legacy = normalizeUser(value, authUser.uid);
  return matchesAuthUser(legacy) ? legacy : null;
};

export const createUserProfile = async (fields, operationId) => {
  const result = await performAdminDataMutation({
    operation: "user.create",
    operationId,
    subjectId: "",
    fields: sanitizeUserMutationFields(fields),
  });
  clearUsersCache();
  return result;
};

export const updateUserProfile = async (userID, fields, operationId) => {
  const result = await performAdminDataMutation({
    operation: "user.update",
    operationId,
    subjectId: String(userID || ""),
    fields: sanitizeUserMutationFields(fields),
  });
  clearUsersCache();
  return result;
};

export const archiveUserProfile = async (userID, operationId) => {
  const result = await performAdminDataMutation({
    operation: "user.delete",
    operationId,
    subjectId: String(userID || ""),
    fields: {},
  });
  clearUsersCache();
  return result;
};
